import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useStore } from '../../stores';
import type { ThemeId, ThemePreference } from './types';
import { THEMES } from './types';

interface ThemeContextValue {
  theme: ThemeId;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  monacoTheme: string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ThemeId {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(preference: ThemePreference): ThemeId {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  orgDefaultTheme?: ThemeId;
}

export function ThemeProvider({ children, orgDefaultTheme }: ThemeProviderProps) {
  const { themePreference, setThemePreference } = useStore();

  // Resolve effective theme preference (user > org > default)
  const effectivePreference = themePreference || orgDefaultTheme || 'system';

  // Resolve to actual theme
  const theme = useMemo(() => resolveTheme(effectivePreference), [effectivePreference]);

  // Get Monaco theme for the resolved theme
  const monacoTheme = useMemo(() => THEMES[theme].monacoTheme, [theme]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove('light', 'dark', 'midnight');

    // Add the resolved theme class
    root.classList.add(theme);
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    if (effectivePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const newTheme = getSystemTheme();
      const root = document.documentElement;
      root.classList.remove('light', 'dark', 'midnight');
      root.classList.add(newTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [effectivePreference]);

  const contextValue: ThemeContextValue = {
    theme,
    themePreference: effectivePreference,
    setThemePreference,
    monacoTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
